import { HttpError } from './HttpError';

/**
 * RestError class for compatibility with log-utils that checks for RestError type
 */
export class RestError extends HttpError {
  restCode?: string;

  constructor(...args: unknown[]) {
    super(...args);
    this.name = 'RestError';
  }
}
RestError.prototype.statusCode = 500;
RestError.prototype.code = 'Error';

/**
 * The shape a search client throws on a non-2xx response.
 *
 * Duck-typed rather than imported: this package sits below
 * `@opensearch-project/opensearch`. `meta` carries the response on v8
 * clients; the flattened fields are the v7 and OpenSearch spelling.
 */
type SearchResponseErrorShape = {
  name?: string;
  statusCode?: number;
  meta?: { statusCode?: number; body?: unknown };
  body?: unknown;
};

/** Cap on the fallback body dump, so one error cannot flood a log stream. */
const maxSerializedBodyLength = 2000;

const isSearchResponseError = (
  err: unknown
): err is SearchResponseErrorShape => {
  if (typeof err !== 'object' || err === null) {
    return false;
  }
  const candidate = err as SearchResponseErrorShape;
  return (
    candidate.name === 'ResponseError' &&
    (candidate.meta !== undefined ||
      candidate.body !== undefined ||
      candidate.statusCode !== undefined)
  );
};

/**
 * Pulls the cluster's own explanation out of a search `ResponseError`.
 *
 * These errors carry the literal message "Response Error" and nothing else, so
 * the generic name/message/stack fields leave a throttled write, a mapping
 * conflict and an oversized payload indistinguishable in the logs. The status
 * and reason live on the response body, which is dropped otherwise.
 */
const describeSearchResponse = (
  err: SearchResponseErrorShape
): Record<string, unknown> => {
  const body = err.meta?.body ?? err.body;
  const errorBody = (
    typeof body === 'object' && body !== null
      ? (body as { error?: unknown }).error
      : undefined
  ) as
    | { type?: string; reason?: string; caused_by?: { reason?: string } }
    | undefined;

  const serializedBody =
    errorBody === undefined && body !== undefined
      ? // Shape we don't recognise — keep a bounded dump rather than log nothing.
        JSON.stringify(body)?.slice(0, maxSerializedBodyLength)
      : undefined;

  return {
    statusCode: err.meta?.statusCode ?? err.statusCode,
    type: errorBody?.type,
    reason: errorBody?.reason,
    causedBy: errorBody?.caused_by?.reason,
    body: serializedBody,
  };
};

/**
 * Bunyan serializer for Fragment errors.
 * Compatible with restify-errors bunyanSerializer behavior.
 *
 * @param err - The error to serialize
 * @returns Serialized error object for logging
 */
export const bunyanSerializer = (
  err: Error | HttpError | RestError | any
): any => {
  if (!err || !err.stack) {
    return err;
  }

  const obj: any = {
    message: err.message,
    name: err.name,
    stack: err.stack,
  };

  // Add HTTP error specific fields if available
  if (err instanceof HttpError) {
    if (err.statusCode) {
      obj.statusCode = err.statusCode;
    }
    if (err.code) {
      obj.code = err.code;
    }
    if (err.body) {
      obj.body = err.body;
    }

    // Add VError info/context
    const { context } = err;
    if (context && Object.keys(context).length > 0) {
      obj.context = context;
    }
  }

  // Add RestError specific fields
  if (err instanceof RestError && err.restCode) {
    obj.restCode = err.restCode;
  }

  if (isSearchResponseError(err)) {
    obj.searchResponse = describeSearchResponse(err);
  }

  return obj;
};
